
import * as api from 'api';
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';

export const fileStatus = {
	pending: 'pending',
	processing: 'processing',
	completed: 'completed',
	error: 'failed',
};

export const useReportFiles = () => {
	const interval = useRef(null);

	const [fetchFiles, files] = api.mw.file.fileReportList.useLazyQuery({
		offset: 0,
		limit: 100,
	});
	const [deleteReport] = api.mw.file.fileReportDelete.useMutation();
	const [cancelReport] = api.mw.file.fileReportCancel.useMutation();

	const [pendingFiles, options] = useMemo(() => {
		let pendingFiles = 0;

		const options = files?.data?.result?.map((data) => {
			if ([fileStatus.processing, fileStatus.pending].includes(data.status)) {
				pendingFiles++;
			}

			return {
				data,
			};
		}) || [];

		return [pendingFiles, options];
	}, [files]);

	const stopPooling = () => {
		if (interval.current !== null) {
			clearInterval(interval.current);
		}
	};

	useEffect(() => {
		if (!files.data && !files.isLoading && !files.isError) {
			fetchFiles();
		}

		if (pendingFiles) {
			stopPooling();

			interval.current = setInterval(() => {
				fetchFiles();
			}, 3000);
		} else {
			stopPooling();
		}
	}, [files, pendingFiles]);

	const deleteReportFile = useCallback((fileId) => {
		if (!fileId) {
			return Promise.resolve(null);
		}

		return deleteReport({ fileId })
			.then((response) => {
				fetchFiles();

				return response;
			});
	}, [deleteReport, fetchFiles]);

	const cancelReportFile = useCallback((fileId) => {
		if (!fileId) {
			return Promise.resolve(null);
		}

		return cancelReport({ fileId })
			.then((response) => {
				fetchFiles();

				return response;
			});
	}, [cancelReport, fetchFiles]);

	return {
		fetchFiles,
		files,
		pendingFiles,
		options,
		deleteReportFile,
		cancelReportFile,
	};
};

const __exportRequest = {
	diagrammExport: api.mw.request.filterExtended.diagrammExport,
	applyExport: api.mw.request.filterExtended.applyExport,
	reportTemplateExport: api.mw.request.filterExtended.reportTemplateExport,
	address: api.mw.customer.customerAddressesExport,
	appeals: api.mw.appeal.appealCustomerExport,
	all_appeals: api.mw.appeal.appealCustomerExport,
	consultation: api.mw.appeal.appealCustomerExport,
	history_change: api.mw.customer.customerAuditExport,
	appeal_interaction_export: api.mw.appeal.appealProgressInteractionsExport,
};

export const useReportGenerate = (defaultParams = {}, requestKey = 'diagrammExport') => {
	const [makeRequest, response] = __exportRequest[requestKey].useMutation();
	const { fetchFiles } = useReportFiles();

	const generateReport = (params = {}) => {
		return makeRequest({
			...defaultParams,
			...params,
		}).then(() => {
			fetchFiles();
		});
	};

	return {
		generateReport,
		response,
	};
};
